import _ from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { _projectAppNames } from '../../../project/appConfiguration';
import { gEnums } from '../../enums/globalEnums';
import { storageHandlers, storageInitialState, storageReducer } from '../reducers/StorageReducer';
import { AppContext } from './AppProvider';
import { AppSettingsContext } from './AppSettingsContext';
import { DatabaseContext } from './DatabaseContext';
import { EventInfoContext } from './EventInfoContext';
import { ManifestContext } from './ManifestContext';
import { PapsContext } from './PapsContext';
import { CurrentSettingsContext } from './SettingsDataContext';
import { useStorageUrls } from './useHelpers/useStorageUrls';

/** Global Files (Icon, Image, PDF) for the Event taken from the storage */
export const StorageContext = createContext();

/** Global Files (Icon, Image, PDF) for the Event taken from storage */
const StorageProvider = (props) => {

  // appContext
  const appContext = useContext(AppContext)
  const { app_state } = appContext
  const { mainStorage } = app_state ?? {}

  // databaseContext
  const databaseContext = useContext(DatabaseContext);
  const { database_fns } = databaseContext ?? {}

  // eventInfoContext
  const appSettingsContext = useContext(AppSettingsContext)
  const { appSettings_state } = appSettingsContext ?? {}
  const { appConfigSettings } = appSettings_state ?? {}
  const { appNames } = appConfigSettings ?? {}
  const { appName } = appNames ?? {}

  // eventInfoContext
  const eventInfoContext = useContext(EventInfoContext)
  const { eventInfo_state } = eventInfoContext ?? {}
  const { appDataDocuments, clientProfiles } = eventInfo_state ?? {}

  //  manifestContext
  const manifestContext = useContext(ManifestContext);
  const { manifest_handlers, manifest_state } = manifestContext ?? {}
  const { manifestDocName } = manifest_state ?? {}

  // papsContext
  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext
  const { rootPaths, settingsDocName, pathViews } = paps_state ?? {}

  // currentSettingsContext
  const currentSettingsContext = useContext(CurrentSettingsContext)
  const { currentSettings_state } = currentSettingsContext ?? {}
  const { aps_global, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { projectOptions, storageOptions } = aps_global ?? {}
  const { useUnderscorePrefix } = projectOptions ?? {}
  const { appUserCollection, useClientProfileImage, getImageFromProfileProp, profileImageProp } = aps_appUserSettings ?? {}

  let _storageRootPath;
  let _storageRootPath_client = pathViews.clients ? rootPaths.clients.substring(1) : null

  if (pathViews.events) {
    _storageRootPath = rootPaths.events.substring(1)
  } else if (pathViews.clients) {
    _storageRootPath = rootPaths.clients.substring(1)
  }

  const init_state = { database_fns, pathViews, appUserCollection, mainStorage, storageOptions, useClientProfileImage, getImageFromProfileProp, profileImageProp }
  const [storage_state, storageDispatch] = useReducer(storageReducer, storageInitialState(init_state));
  const { appStorageUrlFiles } = storage_state ?? {}

  const storage_handlers = useMemo(
    () => storageHandlers(storageDispatch),
    [storageDispatch]
  );

  const storageInfo = useStorageUrls()

  useEffect(
    () => {
      storage_handlers.handleSet_storageInfo(storageInfo, clientProfiles)
    },
    // eslint-disable-next-line
    [storageInfo, clientProfiles] // appUsers_state
  )

  // update the current manifest
  useEffect(
    () => {
      if (appStorageUrlFiles)
        if (settingsDocName !== manifestDocName) {
          _.forEach(appStorageUrlFiles, (fileGroup, fileGroupKey) => {
            switch (fileGroupKey) {
              case gEnums.storageTypes.manifest:
              case 'manifests':
                manifest_handlers.handleAmmend_currentManifest(fileGroup, aps_global, settingsDocName)
                break;
              default:
              // nothing
            }
          })
        }
    },
    // eslint-disable-next-line
    [appStorageUrlFiles, settingsDocName] // appUsers_state
  )

  useEffect(
    () => {
      if (appStorageUrlFiles && appDataDocuments)
        _.forEach(appStorageUrlFiles, (fileGroup, fileGroupKey) => {
          switch (fileGroupKey) {
            case gEnums.storageTypes.floormap:
            case gEnums.storageTypes.icon:
            case gEnums.storageTypes.image:
              break;
            case 'profile':
              const atts = appDataDocuments['attendees']
              if (atts) {
                _.forEach(fileGroup, (fileGroupItem, fileGroupItemKey) => {
                  const att = _.find(atts, { email: fileGroupItemKey })
                  if (att) {
                    const { urls } = fileGroupItem ?? {}
                    att._imageUrls = urls
                  }
                })
              }
              break;
          }
        })
    },
    // eslint-disable-next-line
    [appStorageUrlFiles, appDataDocuments] // appUsers_state
  )

  const storage_fns = useMemo(
    () => ({
      getIconUrl: (allow, fileName) => {
        if (allow && appStorageUrlFiles && fileName && appStorageUrlFiles.icon && appStorageUrlFiles.icon[fileName]) {
          return appStorageUrlFiles.icon[fileName]
        }
      },
      getAppUrl: (fileCollection, fileName) => {
        let _selectedUrlFiles;
        if (appStorageUrlFiles[fileCollection]) {
          _selectedUrlFiles = appStorageUrlFiles?.[fileCollection]
        } else {
          if (appStorageUrlFiles?._gallery_direct?.[fileCollection]) {
            _selectedUrlFiles = appStorageUrlFiles?._gallery_direct?.[fileCollection]
          } else if (appStorageUrlFiles?.[fileCollection]) {
            _selectedUrlFiles = appStorageUrlFiles?.[fileCollection]
          }
        }

        let _selectedUrlFile = _selectedUrlFiles?.[fileName]
        if (_selectedUrlFile?.profile) {
          _selectedUrlFile = _selectedUrlFile?.profile
        }
        const { urls } = _selectedUrlFile ?? {}
        const { full } = urls ?? {}
        return full
      },
      // LOOK
      getImageUrl: (fileName, fullImage, storageType, callback) => {
        const lastDotIndex = fileName.lastIndexOf('.');
        const fileNameS = lastDotIndex !== -1
          ? [fileName.slice(0, lastDotIndex), fileName.slice(lastDotIndex + 1)]
          : [fileName];
        const file_name = fileNameS[0]
        if (appStorageUrlFiles && appStorageUrlFiles[storageType] && appStorageUrlFiles[storageType][file_name]) {
          // const { full, thumbnail } = appStorageUrlFiles[storageType][fileName]
          const { urls } = appStorageUrlFiles[storageType][file_name]
          const { full, thumbnail } = urls ?? {}
          callback({ url: fullImage ? full : thumbnail })
        } else {
          callback()
        }
      },
      getImageUrls: (app_banners, bannerUrlSize, storageType, callback) => {
        let { banners } = app_banners ?? {}
        if (appStorageUrlFiles && appStorageUrlFiles[storageType]) {
          const _bannerUrls = []
          const _banners = _.isArray(banners) ? banners : [banners]
          if (_banners) {
            _banners.forEach(banner => {
              if (appStorageUrlFiles[storageType][banner]) {
                const { urls } = appStorageUrlFiles[storageType][banner]
                const { full, thumbnail } = urls ?? {}
                _bannerUrls.push(bannerUrlSize === gEnums.banner ? full : thumbnail)
              }
            })
            callback({ urls: _bannerUrls })
          }
        } else {
          callback()
        }
      },
    }),
    [appStorageUrlFiles]
  );

  const providerContext = () => <StorageContext.Provider value={{ storage_state, storage_handlers, storage_fns }}>
    {props.children}
  </StorageContext.Provider>

  return appStorageUrlFiles ? providerContext() : <div></div>

}

export default StorageProvider